import React, { useCallback, useEffect, useState } from "react";
import { Hourglass, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const ACTIONS = [
  { v: "keep_monitoring", l: "Keep monitoring" },
  { v: "escalate", l: "Escalate" },
  { v: "sync_from_stripe", l: "Re-check with Stripe" },
];

function ReviewRow({ r, run, reload }) {
  const [action, setAction] = useState("keep_monitoring");
  const [note, setNote] = useState("");
  const submit = () => run(async () => {
    await api.post(`/admin/earnings/refund-reviews/${encodeURIComponent(r.refund_id)}/decision`, { action, note });
    setNote(""); reload();
  }, "Decision recorded");
  return (
    <div className="rounded-2xl border border-white/8 bg-[#141113]/70 p-4 space-y-3" data-testid={`refund-review-${r.refund_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono text-[#FBF8F3]">{r.refund_id}</span>
        <span className="rounded-full border border-[#D4AF37]/30 px-2 py-0.5 text-xs text-[#D4AF37]" data-testid={`refund-review-stripe-status-${r.refund_id}`}>Stripe: {r.status}</span>
        <span className="rounded-full bg-[#541224]/60 px-2 py-0.5 text-xs text-[#FBF8F3]" data-testid={`refund-review-status-${r.refund_id}`}>Review: {r.review?.status}</span>
      </div>
      <div className="grid sm:grid-cols-4 gap-2 text-xs text-[#9E978E]">
        <div>Amount: <span className="text-[#D8D2C9]">${((r.amount_cents || 0) / 100).toFixed(2)}</span></div>
        <div>Waiting since: <span className="text-[#D8D2C9]">{(r.awaiting_since || r.created_at || "").slice(0, 10)}</span></div>
        <div>Reason: <span className="text-[#D8D2C9]">{r.review?.reason}</span></div>
        <div>Session: <span className="text-[#D8D2C9] font-mono">{(r.session_id || "").slice(0, 18)}…</span></div>
      </div>
      {(r.review?.decisions || []).length > 0 && (
        <ul className="text-xs text-[#9E978E] space-y-1">
          {r.review.decisions.map((d, i) => <li key={i}>{d.at?.slice(0, 16)} · {d.action} · {d.note}</li>)}
        </ul>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <select data-testid={`refund-review-action-${r.refund_id}`} value={action} onChange={(e) => setAction(e.target.value)}
          className="rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3]">
          {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
        </select>
        <input data-testid={`refund-review-note-${r.refund_id}`} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Reason (required, min 10 characters)"
          className="flex-1 rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3]" />
        <button data-testid={`refund-review-submit-${r.refund_id}`} disabled={note.trim().length < 10} onClick={submit}
          className="rounded-full bg-[#D4AF37] text-[#0C0A0A] px-4 py-2 text-sm font-medium disabled:opacity-40 transition-opacity">Record decision</button>
      </div>
    </div>
  );
}

export default function AdminRefundReviews({ run }) {
  const [data, setData] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/earnings/refund-reviews", { params: { include_closed: showClosed } }).then((r) => setData(r.data)).catch(() => setData({ reviews: [] }));
  }, [showClosed]);
  useEffect(() => { load(); }, [load]);
  const scan = async () => {
    try {
      const r = await api.post("/admin/earnings/refund-reviews/scan");
      toast.success(r.data.flagged ? `Scan complete: ${r.data.flagged} refund(s) newly flagged` : "Scan complete: nothing new to flag");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
  };
  return (
    <div className="space-y-5" data-testid="admin-refund-reviews">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-[#9E978E] max-w-2xl flex gap-2"><Hourglass size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />Refunds still pending or waiting on the customer after {data?.review_after_days ?? 7} days are flagged here. Flagging never changes the Stripe status or any credits; the affected credits and rewards stay restricted until Stripe resolves the refund.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-[#9E978E] flex items-center gap-1.5">
            <input data-testid="refund-reviews-show-closed" type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="accent-[#D4AF37]" /> Show closed
          </label>
          <button data-testid="refund-reviews-scan-btn" onClick={scan} className="rounded-full border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-2 text-sm flex items-center gap-1.5 hover:bg-[#541224]/40 transition-colors"><RefreshCw size={14} /> Scan now</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-[#9E978E]" data-testid="refund-reviews-summary">
        <span>Refunds awaiting resolution: <span className="text-[#FBF8F3]">{data?.awaiting_refunds ?? "—"}</span></span>
        <span>Last scan: <span className="text-[#FBF8F3]">{data?.last_scan ? `${data.last_scan.at.slice(0, 16)} (${data.last_scan.trigger})` : "never"}</span></span>
      </div>
      {data && data.reviews.length === 0 && <div className="text-sm text-[#9E978E]" data-testid="refund-reviews-empty">No refunds need review.</div>}
      {data?.reviews.map((r) => <ReviewRow key={r.refund_id} r={r} run={run} reload={load} />)}
    </div>
  );
}
